import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { AnchorComponent, ButtonComponent } from "../../../components/elements";
import { Modal, Row, Col, Form, Table } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { Link } from "react-router-dom";
import Swal from 'sweetalert2';
import '../App/modalstyle.css';

export default function SurgeryIndex() {
    const { t } = useContext(TranslatorContext);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alertModal, setAlertModal] = useState(false);
    const [currentId, setCurrentId] = useState(null);

    // Store Modal States
    const [storeModal, setStoreModal] = useState(false);
    const [storeForm, setStoreForm] = useState({
        name: '',
        price: ''
    });
    const [storeErrors, setStoreErrors] = useState({});
    const [storeLoading, setStoreLoading] = useState(false);

    // Update Modal States
    const [updateModal, setUpdateModal] = useState(false);
    const [updateForm, setUpdateForm] = useState({
        id: null,
        name: '',
        price: ''
    });
    const [updateErrors, setUpdateErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState(false);

    useEffect(() => {
        fetchSurgery();
    }, []);

    const fetchSurgery = () => {
        setLoading(true);

        const token = localStorage.getItem("authToken");

        axios.get(`${process.env.REACT_APP_API_URL}/surgery`, {
            headers: {
                Authorization: `Bearer ${token}`
            }
        })
            .then(res => setData(res.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
    };

    // Store Modal Functions
    const openStoreModal = () => {
        setStoreForm({ name: '', price: '' });
        setStoreErrors({});
        setStoreModal(true);
    };

    const handleStoreChange = (e) => {
        const { name, value } = e.target;
        setStoreForm(prev => ({ ...prev, [name]: value }));
        if (storeErrors[name]) {
            setStoreErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleStoreSubmit = async (e) => {
        e.preventDefault();
        setStoreLoading(true);
        setStoreErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/surgery`,
                storeForm,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            if (response.data.surgery) {
                fetchSurgery();
                setStoreModal(false);
                setStoreForm({ name: '', price: '' });

                // Success toast
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Surgery created successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setStoreErrors(error.response.data.errors || {});

                // Validation error toast
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error creating surgery:', error);

                // Error toast
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to create surgery'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setStoreLoading(false);
        }
    };

    // Update Modal Functions
    const openUpdateModal = (item) => {
        setUpdateForm({
            id: item.id,
            name: item.name,
            price: item.price || ''
        });
        setUpdateErrors({});
        setUpdateModal(true);
    };

    const handleUpdateChange = (e) => {
        const { name, value } = e.target;
        setUpdateForm(prev => ({ ...prev, [name]: value }));
        if (updateErrors[name]) {
            setUpdateErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setUpdateLoading(true);
        setUpdateErrors({});

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/surgery-update/${updateForm.id}`,
                {
                    name: updateForm.name,
                    price: updateForm.price
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`
                    }
                }
            );

            if (response.data.surgery) {
                fetchSurgery();
                setUpdateModal(false);

                // Success toast
                Swal.fire({
                    icon: 'success',
                    title: t('success'),
                    text: t('Surgery updated successfully'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setUpdateErrors(error.response.data.errors || {});

                // Validation error toast
                Swal.fire({
                    icon: 'error',
                    title: t('validation_error'),
                    text: t('Please check the form for errors'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else if (error.response?.status === 404) {
                setUpdateErrors({ general: [error.response.data.error] });

                // Not found error toast
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: error.response.data.error || t('Surgery not found'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            } else {
                console.error('Error updating surgery:', error);

                // Error toast
                Swal.fire({
                    icon: 'error',
                    title: t('error'),
                    text: t('Failed to update surgery'),
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true
                });
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleDelete = (id) => {
        // SweetAlert confirmation for delete
        Swal.fire({
            title: t('Are you sure?'),
            text: t("Want to delete this surgery?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('delete'),
            cancelButtonText: t('cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("authToken");
                    const response = await axios.delete(
                        `${process.env.REACT_APP_API_URL}/surgery/${id}`,
                        {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        }
                    );

                    if (response.data.success || response.status === 200) {
                        fetchSurgery();

                        // Success toast
                        Swal.fire({
                            icon: 'success',
                            title: t('deleted'),
                            text: t('Surgery deleted successfully'),
                            toast: true,
                            position: 'top-end',
                            showConfirmButton: false,
                            timer: 3000,
                            timerProgressBar: true
                        });
                    }
                } catch (error) {
                    console.error('Error deleting surgery:', error);

                    // Error toast
                    Swal.fire({
                        icon: 'error',
                        title: t('error'),
                        text: error.response?.data?.message || t('Failed to delete surgery'),
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 3000,
                        timerProgressBar: true
                    });
                }
            }
        });

        setAlertModal(false);
    };

    if (loading) return <PageLayout><p>{t("loading")}...</p></PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('surgery')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('surgery')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('list')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        {/* Add New Button */}
                        <div className="mc-card-header d-flex justify-content-between align-items-center p-3">
                            <h5 className="mc-card-title mb-0">{t('surgery list')}</h5>
                            <ButtonComponent
                                type="button"
                                className="btn btn-primary d-flex align-items-center"
                                onClick={openStoreModal}
                            >
                                <i className="material-icons">add</i>
                                {t('add Surgery')}
                            </ButtonComponent>
                        </div>

                        <Row>
                            <Col xl={12}>
                                <div className="mc-card">
                                    <div className="table-responsive-wrapper">
                                        <Table responsive className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th>
                                                        <div className="mc-table-check">
                                                            <p>#</p>
                                                        </div>
                                                    </th>
                                                    <th>{t('name')}</th>
                                                    <th>{t('price')}</th>
                                                    <th>{t('actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="4" className="text-center">
                                                            {t('No surgery records found')}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    data.map((item, index) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <div className="mc-table-check">
                                                                    <p>{index + 1}</p>
                                                                </div>
                                                            </td>
                                                            <td>{item.name}</td>
                                                            <td>{item.price}</td>
                                                            <td>
                                                                <div className="mc-table-action">
                                                                    <ButtonComponent
                                                                        type="button"
                                                                        className="material-icons edit"
                                                                        onClick={() => openUpdateModal(item)}
                                                                        title="Edit"
                                                                    >
                                                                        edit
                                                                    </ButtonComponent>
                                                                    {/* <ButtonComponent
                                                                    type="button"
                                                                    className="material-icons delete"
                                                                    onClick={() => handleDelete(item.id)}
                                                                    title="Delete"
                                                                >
                                                                    delete
                                                                </ButtonComponent> */}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </Table>

                                        {/* Remove the old delete confirmation modal since we're using SweetAlert now */}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>

            {/* Store Modal - Add Surgery */}
            <Modal
                show={storeModal}
                onHide={() => setStoreModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">{t('add surgery')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleStoreSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold">{t('name')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={storeForm.name}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.name}
                                placeholder={t('Enter surgery name')}
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('price')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                className="custom-input"
                                value={storeForm.price}
                                onChange={handleStoreChange}
                                isInvalid={!!storeErrors.price}
                                placeholder={t('Enter price')}
                            />
                            <Form.Control.Feedback type="invalid">
                                {storeErrors.price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setStoreModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary px-4"
                            disabled={storeLoading}
                        >
                            {storeLoading && <span className="spinner-border spinner-border-sm me-2"></span>}
                            {t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Update Modal - Edit Surgery */}
            <Modal
                show={updateModal}
                onHide={() => setUpdateModal(false)}
                centered
                dialogClassName="custom-modal-dialog"
                contentClassName="custom-modal-content"
            >
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">{t('edit surgery')}</Modal.Title>
                </Modal.Header>

                <Form onSubmit={handleUpdateSubmit}>
                    <Modal.Body className="custom-modal-body">
                        {/* General Error Alert */}
                        {updateErrors.general && (
                            <div className="alert alert-danger d-flex align-items-center mb-4">
                                <i className="material-icons me-2">error_outline</i>
                                {updateErrors.general}
                            </div>
                        )}

                        <Form.Group className="mb-4">
                            <Form.Label className="fw-bold">{t('name')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                className="custom-input"
                                value={updateForm.name}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.name}
                                placeholder={t('Enter surgery name')}
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.name?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">{t('price')} <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                className="custom-input"
                                value={updateForm.price}
                                onChange={handleUpdateChange}
                                isInvalid={!!updateErrors.price}
                                placeholder={t('Enter price')}
                            />
                            <Form.Control.Feedback type="invalid">
                                {updateErrors.price?.[0]}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer className="custom-modal-footer">
                        <ButtonComponent
                            type="button"
                            className="btn btn-secondary px-4"
                            onClick={() => setUpdateModal(false)}
                        >
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent
                            type="submit"
                            className="btn btn-primary px-4"
                            disabled={updateLoading}
                        >
                            {updateLoading ? (
                                <span className="spinner-border spinner-border-sm me-2"></span>
                            ) : (
                                <i className="material-icons align-middle me-1" style={{ fontSize: '18px' }}>sync</i>
                            )}
                            {t('update')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}